import React, { useState } from "react";
import PropTypes from "prop-types";
import ImageCropped from "views_shared/app/javascript/components/ImageCropped";
import qahoy from "views_shared/app/javascript/scripts/qahoy";
import { expertUrl, prProfileUrl } from "views_shared/app/javascript/utils/urls";
import Tag from "../../profiles/Tag";

const MAX_HASHTAGS = 10;

// Event name predates the card-to-row conversion; kept verbatim to preserve the qahoy analytics series.
const HASHTAG_CLICK_EVENT = "Experts/ExpertCardMinimal#clickHashtag";

// The banner's chips reach the expert index's hashtag filter, which is where this population is
// searchable.
function expertHashtagUrl(hashtagName) {
  return `/experts?hashtag=${encodeURIComponent(hashtagName)}`;
}

// Only a promoter has a profile of their own to link to; every other kind of person on a company's
// roster (including a row with no class at all) lives at /experts.
// The Promoter branch (/profiles/:slug) is the generic cross-type profile route (see `profileUrl` in
// views_shared/utils/urls) — out of scope here since it carries no `/experts/` literal to migrate;
// only the plain-expert branch routes through the V1-aware expertUrl helper.
// V1-COMPAT: on V2 (V2_URL unset) the Promoter branch stays the exact `/profiles/:slug` path —
// prProfileUrl's own V2-native fallback is `/pr_profiles/:slug`, a DIFFERENT page, so it can't be
// used unconditionally here. Only when V1-hosted does the Promoter link need to become V1-aware.
function highlightedExpertPath({ profileType, slug, migrateId }) {
  if (profileType !== "Id::Profile::Promoter") {
    return expertUrl({ slug, migrate_id: migrateId });
  }
  return globalThis.Qwoted?.V2_URL ? prProfileUrl({ slug, migrate_id: migrateId }) : `/profiles/${slug}`;
}

// The caller batches this card's tags in with the rest of the roster's (both axes, unioned) rather
// than each highlighted card self-fetching its own — see the roster's own tag-hydration comment for
// why. The chip destination, the 10-chip cap, and the click event must all match the roster's chips.
function renderTags(tags) {
  if (!tags || tags.length === 0) return null;

  return (
    <div>
      {tags.slice(0, MAX_HASHTAGS).map((tag) => (
        <Tag
          key={tag.hashtag_id}
          className="mb-1 me-1"
          href={expertHashtagUrl(tag.hashtag_name)}
          onClick={() => qahoy.track(HASHTAG_CLICK_EVENT)}
        >
          {tag.hashtag_name}
        </Tag>
      ))}
    </div>
  );
}

function CompanyHighlightedExpert({
  description = undefined,
  id,
  name,
  profileImageUrl,
  profileType = undefined,
  slug,
  migrateId = undefined,
  tags = null,
  title = undefined,
}) {
  const [seeMoreDescription, setSeeMoreDescription] = useState(false);

  const renderDescription = () => {
    const descriptionLength = description?.length;
    if (!descriptionLength) return null;

    const truncDescriptionLength = 350;

    if (seeMoreDescription) {
      return (
        <>
          {description}
          <br />
          <button onClick={() => setSeeMoreDescription(!seeMoreDescription)} type="button" className="btn btn-link">
            Hide
          </button>
        </>
      );
    }

    if (descriptionLength < truncDescriptionLength) {
      return description;
    }

    return (
      <>
        {`${description.slice(0, truncDescriptionLength)}...`}
        <button onClick={() => setSeeMoreDescription(!seeMoreDescription)} type="button" className="btn btn-link">
          See more
        </button>
      </>
    );
  };

  const expertPath = highlightedExpertPath({ profileType, slug, migrateId });

  return (
    <div className="card w-100 h-100 mx-3 mb-3 p-1" key={id}>
      <div className="card-body h-100 d-flex align-items-stretch gx-0">
        <div className="pe-3">
          <ImageCropped rounded wrapperClasses="me-1" src={profileImageUrl} alt={name} size={250} fallbackToInitials />
        </div>

        <div>
          <h3 className="mb-1">
            <a href={expertPath} className="fw-bold cursor-pointer">
              {name}
            </a>
          </h3>

          {title && <span className="text-secondary">{title}</span>}

          {description && <p className="mt-2">{renderDescription()}</p>}

          {renderTags(tags)}
        </div>
      </div>
    </div>
  );
}

CompanyHighlightedExpert.propTypes = {
  description: PropTypes.string,
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  profileImageUrl: PropTypes.string.isRequired,
  profileType: PropTypes.string,
  slug: PropTypes.string.isRequired,
  migrateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tags: PropTypes.arrayOf(PropTypes.shape({ hashtag_id: PropTypes.number, hashtag_name: PropTypes.string })),
  title: PropTypes.string,
};

export default CompanyHighlightedExpert;
